<script lang="ts" setup>
import type { Product } from '~/models';

const props = defineProps<{
  product: Product;
}>();

const cartStore = useCartStore();

const textPrice = computed<string>(() => useCurrency().format(props.product.price));

const quantity = computed<number>(() => {
  return cartStore.items
    .filter((item) => item.productUuid === props.product.uuid)
    .reduce((acc, curr) => acc + curr.quantity, 0);
});
</script>

<template>
  <li class="
    product-item
    rounded-md
    text-base
    gap-1
    bg-card
    hover::after:bg-card
    border
    border-card
    py-2
    px-4
    cursor-pointer
    relative
    select-none
    overflow-hidden
    text-lg
    font-roboto-bold
  ">
    <span
      class="
        product-item__name
        whitespace-nowrap
        overflow-hidden
        text-ellipsis
      "
      data-test="name"
    >
      {{ props.product.name }}
    </span>

    <span
      v-if="quantity"
      class="product-item__quantity text-green-700 text-sm"
      data-test="quantity"
    >
      {{ `x ${quantity}` }}
    </span>

    <span
      v-if="props.product.description"
      class="product-item__description whitespace-nowrap overflow-hidden text-ellipsis text-sm subtext-card"
      data-test="description"
    >
      {{ props.product.description }}
    </span>

    <span
      class="product-item__price text-company-1"
      data-test="price"
    >
      {{ textPrice }}
    </span>
  </li>
</template>

<style lang="scss" scoped>
.product-item {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'name quantity'
    'description description'
    'price price';

  &:active::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #00000025;
  }

  &__name {
    grid-area: name;
  }

  &__quantity {
    grid-area: quantity;
  }

  &__description {
    grid-area: description;
  }

  &__price {
    grid-area: price;
  }
}
</style>
